import React from "react";
import { Link } from "react-router-dom";
import { 
  Truck, ShieldCheck, Clock, MapPin, Phone, 
  ArrowRight, Star, Users, Box, CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_56ae7323-5fde-47d2-aaef-037899d4e104/artifacts/v2heysoi_1769273488975.mp4";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/c5to8wjs_1769061921315.png";
const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/7m7lw26g_1768944737752.jpg";
const SERVICES_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/wk29609y_1768943867734.jpg";
const TRUCK_IMAGE = "https://customer-assets.emergentagent.com/job_cornwall-movers/artifacts/no7liyqw_1769062093447.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_movewithtony/artifacts/0c98olj8_c413a017a11bcba_file_000000009aec61fba9d62adbd361f5c9_wm.png";

const HomePage = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: "Fully Insured",
      description: "Your belongings are protected throughout the entire move."
    },
    {
      icon: Users,
      title: "Professional Team",
      description: "Experienced movers who treat your items with care."
    },
    {
      icon: Clock,
      title: "Reliable Service",
      description: "On-time arrivals and efficient moving process."
    },
    {
      icon: MapPin,
      title: "Local Experts",
      description: "We know Cornwall and SDG inside and out."
    }
  ];

  const services = [
    {
      title: "Residential Moving",
      description: "Full-service home moving for apartments, houses, and condos.",
      image: SERVICES_IMAGE
    },
    {
      title: "Commercial Moving",
      description: "Office relocations with minimal downtime for your business.",
      image: HERO_IMAGE
    },
    {
      title: "Specialty Items",
      description: "Piano, pool tables, and heavy item specialists.",
      image: TRUCK_IMAGE
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            poster={HERO_IMAGE}
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0047AB]/95 via-[#0047AB]/85 to-[#003380]/75"></div>
        </div>

        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <span className="bg-slate-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                CORNWALL'S TRUSTED MOVERS
              </span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white font-['Barlow_Condensed'] uppercase mb-6 leading-tight">
              Move Your Way<br />
              <span className="text-slate-200">Stress Free</span>
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-lg">
              Professional, friendly, and fully insured local movers serving 
              Cornwall and SDG for over 10 years. Let us handle the heavy lifting.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/quote" data-testid="hero-quote-btn">
                <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold text-lg py-6 px-8 w-full sm:w-auto">
                  Get Free Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/booking" data-testid="hero-book-btn">
                <Button className="bg-slate-600 hover:bg-slate-700 text-white font-bold text-lg py-6 px-8 w-full sm:w-auto">
                  Book Your Move
                </Button>
              </Link>
            </div>
            {/* Quick Contact */}
            <div className="mt-10 flex items-center gap-4">
              <a 
                href="tel:613-362-8180" 
                className="flex items-center gap-2 text-white hover:text-slate-200 transition-colors"
                data-testid="hero-phone"
              >
                <div className="bg-white/20 p-3 rounded-full">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-white/70">Call Tony Now</p>
                  <p className="text-xl font-bold">613-362-8180</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-all"
                data-testid={`feature-card-${index}`}
              >
                <div className="bg-[#0047AB]/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-[#0047AB]" />
                </div>
                <h3 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 md:py-32" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={ABOUT_IMAGE} 
                alt="Tony Moves It Team" 
                className="rounded-2xl shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#0047AB] text-white p-6 rounded-xl shadow-lg">
                <p className="text-4xl font-bold font-['Barlow_Condensed']">10+</p>
                <p className="text-sm">Years Experience</p>
              </div>
            </div>
            <div>
              <p className="text-[#0047AB] font-semibold mb-3 uppercase tracking-wide">About Us</p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 uppercase">
                Setting The Standard<br />
                <span className="text-[#0047AB]">Moving Cornwall Forward</span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Tony Moves It has been serving Cornwall and SDG for over a year with 
                amazing customer reviews and satisfaction. Tony Russell (owner), 
                previously with Mr Move It, has started his own moving business adventure.
              </p>
              <p className="text-lg text-slate-600 mb-8">
                With 10+ years in the business, Tony and crew will get you moved 
                from A to B stress-free, guaranteed. We're a proud local company 
                dedicated to making your move fast, friendly, and completely worry-free.
              </p>
              <ul className="space-y-3 mb-8">
                {["Fair & Transparent Pricing", "Professional & Courteous Team", "Fully Licensed & Insured", "Local Knowledge & Expertise"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0047AB]" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" data-testid="about-learn-more">
                <Button className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold">
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-slate-900" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-slate-400 font-semibold mb-3 uppercase tracking-wide">Our Services</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-white uppercase">
              What We Offer
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl"
                data-testid={`service-preview-${index}`}
              >
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold font-['Barlow_Condensed'] text-white mb-2 uppercase">
                    {service.title}
                  </h3>
                  <p className="text-slate-300">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" data-testid="services-view-all">
              <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold">
                View All Services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0047AB]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-['Barlow_Condensed'] text-white uppercase mb-6">
            Ready to Move?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get your free quote today or book your move directly. 
            We're here to make your moving experience stress-free!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote" data-testid="cta-quote-btn">
              <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold text-lg py-6 px-8 w-full sm:w-auto">
                Get Free Quote
              </Button>
            </Link>
            <Link to="/booking" data-testid="cta-book-btn">
              <Button className="bg-slate-700 hover:bg-slate-800 text-white font-bold text-lg py-6 px-8 w-full sm:w-auto">
                Book Now
              </Button>
            </Link>
          </div>
          <p className="text-white/70 mt-6">
            *Free quote tool provides an estimate. For a full quote, call Tony directly at{" "}
            <a href="tel:613-362-8180" className="text-white font-bold hover:underline">
              613-362-8180
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
